import { CallState } from "@b2bportal/core-types";
import type {
  Lodging,
  LodgingSelection,
  RoomProduct,
} from "@b2bportal/lodging-api";
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import { initialHotelShopState } from "./initialState";
import {
  fetchHotelsShop,
  type FetchShopFulfillResponse,
  fetchHomesShop,
  type FetchHomesShopFulfillResponse,
} from "./thunk";
import type { ShopSearch } from "./types";

export const hotelShopSlice = createSlice({
  name: "hotelShop",
  initialState: initialHotelShopState,
  reducers: {
    setShopSearch: (state, action: PayloadAction<ShopSearch>) => {
      state.shopSearch = action.payload;
    },
    clearShopSearch: (state) => {
      state.shopSearch = undefined;
      state.shop = undefined;
      state.shopCallState = CallState.NotCalled;
    },
    setLodgingUnavailable: (state, action: PayloadAction<boolean>) => {
      state.isLodgingUnavailable = action.payload;
    },
    setLodging: (state, action: PayloadAction<Lodging>) => {
      state.lodging = action.payload;
      state.availabilityCallState = CallState.Success;
    },
    setSelectedRoomProduct: (
      state,
      action: PayloadAction<RoomProduct | undefined>
    ) => {
      state.selectedRoomProduct = action.payload;
    },
    setLodgingSelection: (state, action: PayloadAction<LodgingSelection>) => {
      state.lodgingSelection = action.payload;
    },
    setHotelsShop: (state, action: PayloadAction<FetchShopFulfillResponse>) => {
      state.shopCallState = CallState.Success;
      state.shop = action.payload.shop;
      state.shopSearch = action.payload.search;
      state.bestOffer = action.payload.bestOffer;
      state.availabilityTrackingProperties =
        action.payload.availabilityTrackingProperties;
    },
    setHotelsShopPending: (state) => {
      state.shopCallState = CallState.InProcess;
      state.availabilityCallState = CallState.InProcess;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchHotelsShop
      .addCase(fetchHotelsShop.pending, (state) => {
        state.shopCallState = CallState.InProcess;
        state.availabilityCallState = CallState.InProcess;
      })
      .addCase(
        fetchHotelsShop.fulfilled,
        (state, action: PayloadAction<FetchShopFulfillResponse>) => {
          state.shopCallState = CallState.Success;
          state.shop = action.payload.shop;
          state.shopSearch = action.payload.search;
          state.bestOffer = action.payload.bestOffer;
          state.availabilityTrackingProperties =
            action.payload.availabilityTrackingProperties;
        }
      )
      .addCase(fetchHotelsShop.rejected, (state) => {
        state.shopCallState = CallState.Failed;
        state.availabilityCallState = CallState.Failed;
      })
      .addCase(fetchHomesShop.pending, (state) => {
        state.shopCallState = CallState.InProcess;
        state.availabilityCallState = CallState.InProcess;
      })
      .addCase(
        fetchHomesShop.fulfilled,
        (state, action: PayloadAction<FetchHomesShopFulfillResponse>) => {
          state.shopCallState = CallState.Success;
          state.homesShop = action.payload.shop;
          state.shopSearch = action.payload.search;
          state.bestOffer = action.payload.bestOffer;
          state.availabilityTrackingProperties =
            action.payload.availabilityTrackingProperties;
        }
      )
      .addCase(fetchHomesShop.rejected, (state) => {
        state.shopCallState = CallState.Failed;
        state.availabilityCallState = CallState.Failed;
      });
  },
});

export const HotelsShopActions = hotelShopSlice.actions;

export default hotelShopSlice.reducer;
